import { usePeriod } from "@/context/PeriodContext";
import { useI18n } from "@/context/I18nContext";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Calendar, ChevronDown, Lock, Radio } from "lucide-react";

const MONTH_TR = ["Ocak","Şubat","Mart","Nisan","Mayıs","Haziran","Temmuz","Ağustos","Eylül","Ekim","Kasım","Aralık"];
const MONTH_DE = ["Januar","Februar","März","April","Mai","Juni","Juli","August","September","Oktober","November","Dezember"];

function formatKey(key, lang) {
  if (!key) return "—";
  const [y, m] = key.split("-").map((s) => parseInt(s, 10));
  const arr = lang === "de" ? MONTH_DE : MONTH_TR;
  return `${arr[(m || 1) - 1]} ${y}`;
}

function statusPill(status, isLive, t) {
  if (status === "closed") {
    return (
      <Badge variant="outline" className="border-slate-300 text-slate-600 gap-1" data-testid="period-status-closed">
        <Lock className="w-3 h-3" /> {t("period.closed", "Kapalı Ay", "Geschlossen")}
      </Badge>
    );
  }
  if (status === "reopened") {
    return (
      <Badge variant="outline" className="border-amber-300 text-amber-700 gap-1" data-testid="period-status-reopened">
        <Radio className="w-3 h-3" /> {t("period.reopened", "Yeniden Açıldı", "Wieder geöffnet")}
      </Badge>
    );
  }
  if (isLive) {
    return (
      <Badge className="bg-emerald-600 hover:bg-emerald-600 gap-1" data-testid="period-status-live">
        <Radio className="w-3 h-3" /> {t("period.live", "Canlı Ay", "Aktueller Monat")}
      </Badge>
    );
  }
  return null;
}

export default function PeriodSelector({ onClose, onReopen, showActions = true }) {
  const { periods, periodKey, currentPeriodKey, setPeriodKey, selectedPeriod, closePeriod, reopenPeriod } = usePeriod();
  const { lang } = useI18n();
  const t = (_key, tr, de) => (lang === "de" ? de : tr);

  const isCurrent = periodKey === currentPeriodKey;
  const status = selectedPeriod?.status || "live";

  const handleClose = async () => {
    if (!window.confirm(t("period.confirm_close",
      "Bu ayı kapatmak istediğine emin misin? Kapanış bakiyeleri kaydedilecek ve yeni girişler engellenecek.",
      "Diesen Monat wirklich schließen? Schlussbilanzen werden gesichert; Buchungen werden blockiert."))) return;
    try {
      await closePeriod(periodKey);
      onClose?.();
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed to close period");
    }
  };

  const handleReopen = async () => {
    if (!window.confirm(t("period.confirm_reopen",
      "Bu ayı yeniden açmak istediğine emin misin?",
      "Diesen Monat wirklich wieder öffnen?"))) return;
    try {
      await reopenPeriod(periodKey);
      onReopen?.();
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed to reopen period");
    }
  };

  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="period-selector">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="h-9 gap-2 rounded-lg" data-testid="period-selector-trigger">
            <Calendar className="w-4 h-4" />
            <span className="font-semibold">{formatKey(periodKey, lang)}</span>
            <ChevronDown className="w-3.5 h-3.5 opacity-60" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-64 rounded-xl max-h-80 overflow-y-auto">
          <DropdownMenuLabel className="text-xs">
            {t("period.list", "Aylar", "Monate")}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {(periods || []).map((p) => {
            const isLive = p.period_key === currentPeriodKey && p.status !== "closed";
            return (
              <DropdownMenuItem
                key={p.period_key}
                onSelect={() => setPeriodKey(p.period_key)}
                data-testid={`period-item-${p.period_key}`}
                className="flex items-center justify-between gap-2"
              >
                <span className={p.period_key === periodKey ? "font-semibold" : ""}>
                  {formatKey(p.period_key, lang)}
                </span>
                <span className="text-[10px] uppercase tracking-wide">
                  {p.status === "closed"
                    ? t("period.closed", "Kapalı", "Geschl.")
                    : p.status === "reopened"
                    ? t("period.reopened", "Açıldı", "Erneut")
                    : isLive
                    ? t("period.live", "Canlı", "Aktuell")
                    : ""}
                </span>
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>

      {statusPill(status, isCurrent && status !== "closed", t)}

      {showActions && periodKey && (
        status === "closed" ? (
          <Button variant="outline" size="sm" className="h-9 rounded-lg" onClick={handleReopen} data-testid="period-reopen-btn">
            {t("period.reopen", "Yeniden Aç", "Wieder öffnen")}
          </Button>
        ) : !isCurrent ? (
          <Button variant="outline" size="sm" className="h-9 rounded-lg" onClick={handleClose} data-testid="period-close-btn">
            {t("period.close", "Ayı Kapat", "Monat schließen")}
          </Button>
        ) : null
      )}
    </div>
  );
}

export function ClosedPeriodBanner() {
  const { isSelectedClosed, periodKey } = usePeriod();
  const { lang } = useI18n();
  if (!isSelectedClosed) return null;
  const label = lang === "de"
    ? `Der Monat ${periodKey} ist geschlossen — nur Lesezugriff. Öffne ihn wieder, um Änderungen vorzunehmen.`
    : `${periodKey} ayı kapalı — yalnızca okuma modunda. Değişiklik için ayı yeniden aç.`;
  return (
    <div
      data-testid="period-closed-banner"
      className="rounded-lg border border-amber-300 bg-amber-50 text-amber-800 text-sm px-3 py-2 flex items-center gap-2"
    >
      <Lock className="w-4 h-4" /> {label}
    </div>
  );
}
